import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ChevronRight, ChevronLeft, X, Briefcase, PlusCircle, MessageSquare, BookOpen } from 'lucide-react';

interface TourStep {
  title: string;
  description: string;
  icon: React.ReactNode;
  action?: {
    label: string;
    path: string;
  };
}

const tourSteps: TourStep[] = [
  {
    title: 'Bienvenue sur BrazzaBoost!',
    description: 'Votre plateforme de référence pour trouver des opportunités à Brazzaville. Laissez-nous vous faire découvrir les fonctionnalités principales.',
    icon: <Briefcase className="h-12 w-12 text-primary" />,
  },
  {
    title: 'Trouver des Opportunités',
    description: 'Parcourez des centaines d\'offres d\'emploi, stages, formations et bourses. Utilisez les filtres pour affiner votre recherche par type, localisation ou date.',
    icon: <Briefcase className="h-12 w-12 text-primary" />,
    action: {
      label: 'Voir les Opportunités',
      path: '/opportunities',
    },
  },
  {
    title: 'Publier une Opportunité',
    description: 'Vous êtes une organisation? Publiez gratuitement vos offres d\'emploi, stages ou formations. Aidez la jeunesse de Brazzaville à trouver leur voie.',
    icon: <PlusCircle className="h-12 w-12 text-primary" />,
    action: {
      label: 'Publier une Opportunité',
      path: '/publish',
    },
  },
  {
    title: 'Blog & Conseils',
    description: 'Découvrez nos articles pour réussir vos entretiens, améliorer votre CV et développer votre carrière professionnelle.',
    icon: <BookOpen className="h-12 w-12 text-primary" />,
    action: {
      label: 'Lire le Blog',
      path: '/blog',
    },
  },
  {
    title: 'Besoin d\'Aide?',
    description: 'Notre chatbot IA est disponible 24/7 pour répondre à vos questions. Cliquez sur l\'icône de chat en bas à droite pour démarrer une conversation.',
    icon: <MessageSquare className="h-12 w-12 text-primary" />,
  },
];

interface OnboardingTourProps {
  forceOpen?: boolean;
  onClose?: () => void;
}

export const OnboardingTour = ({ forceOpen = false, onClose }: OnboardingTourProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    if (forceOpen) {
      setIsOpen(true);
      setCurrentStep(0);
      return;
    }

    const hasSeenTour = localStorage.getItem('brazzaboost_tour_completed');
    if (!hasSeenTour) {
      // Show tour after a short delay
      const timer = setTimeout(() => {
        setIsOpen(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [forceOpen]);

  const handleClose = () => {
    setIsOpen(false);
    setCurrentStep(0);
    if (!forceOpen) {
      localStorage.setItem('brazzaboost_tour_completed', 'true');
    }
    onClose?.();
  };

  const handleNext = () => {
    if (currentStep < tourSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleClose();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleAction = (path: string) => {
    handleClose();
    navigate(path);
  };

  const currentTourStep = tourSteps[currentStep];

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <div className="flex justify-between items-start">
            <div className="flex-1">
              <DialogTitle className="text-2xl mb-2">{currentTourStep.title}</DialogTitle>
              <DialogDescription className="text-base">
                {currentTourStep.description}
              </DialogDescription>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleClose}
              className="h-8 w-8 -mt-2"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </DialogHeader>

        <div className="flex justify-center py-6">
          {currentTourStep.icon}
        </div>

        {currentTourStep.action && (
          <Button
            onClick={() => handleAction(currentTourStep.action!.path)}
            className="w-full mb-4"
            size="lg"
          >
            {currentTourStep.action.label}
          </Button>
        )}

        <div className="flex items-center justify-between pt-4 border-t">
          <div className="flex items-center gap-2">
            {tourSteps.map((_, idx) => (
              <div
                key={idx}
                className={`h-2 w-2 rounded-full transition-all ${
                  idx === currentStep
                    ? 'bg-primary w-8'
                    : idx < currentStep
                    ? 'bg-primary/50'
                    : 'bg-muted'
                }`}
              />
            ))}
          </div>

          <div className="flex gap-2">
            {currentStep > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={handlePrevious}
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Précédent
              </Button>
            )}
            <Button
              size="sm"
              onClick={handleNext}
            >
              {currentStep < tourSteps.length - 1 ? (
                <>
                  Suivant
                  <ChevronRight className="h-4 w-4 ml-1" />
                </>
              ) : (
                'Terminer'
              )}
            </Button>
          </div>
        </div>

        <p className="text-xs text-center text-muted-foreground mt-2">
          Étape {currentStep + 1} sur {tourSteps.length}
        </p>
      </DialogContent>
    </Dialog>
  );
};
